import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ─── Context ────────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

// ─── Configuração por variante ───────────────────────────────────────────────

const config = {
  success: {
    icon: CheckCircle2,
    classes: 'bg-surface border-success text-text-primary',
    iconClass: 'text-success',
  },
  error: {
    icon: XCircle,
    classes: 'bg-surface border-danger text-text-primary',
    iconClass: 'text-danger',
  },
  warning: {
    icon: AlertTriangle,
    classes: 'bg-surface border-warning text-text-primary',
    iconClass: 'text-warning',
  },
  info: {
    icon: Info,
    classes: 'bg-surface border-info text-text-primary',
    iconClass: 'text-info',
  },
};

// ─── Item de Toast ───────────────────────────────────────────────────────────

function ToastItem({ id, message, variant = 'info', onDismiss }) {
  const { icon: Icon, classes, iconClass } = config[variant] || config.info;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={clsx(
        'flex items-start gap-3 w-full max-w-sm rounded-xl border shadow-lg px-4 py-3.5',
        'animate-slide-right',
        classes,
      )}
    >
      <Icon size={20} className={clsx('shrink-0 mt-0.5', iconClass)} aria-hidden="true" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        aria-label="Fechar notificação"
        className="shrink-0 rounded p-0.5 text-text-muted hover:text-text-primary hover:bg-bg-secondary transition-colors duration-normal focus-visible:ring-2 focus-visible:ring-border-focus"
      >
        <X size={16} />
      </button>
    </div>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timerRefs.current[id]);
    delete timerRefs.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(({ message, variant = 'info', duration = 5000 }) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, variant }]);
    if (duration > 0) {
      timerRefs.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Atalhos por variante
  toast.success = (message, opts) => toast({ message, variant: 'success', ...opts });
  toast.error   = (message, opts) => toast({ message, variant: 'error',   ...opts });
  toast.warning = (message, opts) => toast({ message, variant: 'warning', ...opts });
  toast.info    = (message, opts) => toast({ message, variant: 'info',    ...opts });

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div
          aria-label="Notificações"
          className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 sm:top-5 sm:bottom-auto"
        >
          {toasts.map((t) => (
            <ToastItem key={t.id} {...t} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  return ctx;
}
